import {
  IconBrandBluesky,
  IconBrandGithub,
  IconBrandLinkedin,
  IconBrandX,
} from "@tabler/icons-react";
import clsx from "clsx";
import { BLURB, DPA_PUBLISHED, TITLE, X_PROFILE_URL } from "shared/SharedConstants";
import Logo from "src/components/app/Logo/Logo";
import IconButton from "src/components/common/IconButton/IconButton";
import Link from "src/components/common/Link/Link";
import Typography from "src/components/common/Typography/Typography";
import Label from "src/components/form/Label/Label";
import { STATUS_PAGE_URL } from "src/lib/PlatformStatus";
import { BLUESKY_PROFILE_URL, LINKEDIN_PROFILE_URL } from "src/lib/SocialLinks";
import { useExtracted } from "#i18n";
import { version } from "../../../../package.json";
import styles from "./SiteFooter.module.scss";

export interface SiteFooterProps {
  className?: string;
}

const SiteFooter = (props: SiteFooterProps) => {
  const translateMessage = useExtracted();
  const { className } = props;
  const copyrightYear = import.meta.env.PUBLIC_COPYRIGHT_YEAR;

  return (
    <footer className={clsx(styles.footer, className)}>
      <div className={styles.container}>
        <div className={styles.columns}>
          <div className={clsx(styles.column, styles.brand)}>
            <Link href="/" variant="bare" className={styles.logo}>
              <Logo />
            </Link>
            <Typography variant="bodySmall" tone="muted">
              {BLURB}
            </Typography>
            <div className={styles.social}>
              <IconButton
                href={X_PROFILE_URL}
                {...{ target: "_blank" }}
                label={translateMessage("CalmLens on X (opens in new tab)")}
                labelTooltip={false}
                variant="outline"
                size="lg"
                ripple={false}
                shape="circle"
              >
                <IconBrandX size={20} aria-hidden />
              </IconButton>
              <IconButton
                href={LINKEDIN_PROFILE_URL}
                target="_blank"
                label={translateMessage("CalmLens on LinkedIn (opens in new tab)")}
                labelTooltip={false}
                variant="outline"
                size="lg"
                ripple={false}
                shape="circle"
              >
                <IconBrandLinkedin size={20} aria-hidden />
              </IconButton>
              <IconButton
                href={BLUESKY_PROFILE_URL}
                target="_blank"
                label={translateMessage("CalmLens on Bluesky (opens in new tab)")}
                labelTooltip={false}
                variant="outline"
                size="lg"
                ripple={false}
                shape="circle"
              >
                <IconBrandBluesky size={20} aria-hidden />
              </IconButton>
              <IconButton
                href="https://github.com/CalmLens"
                {...{ target: "_blank" }}
                label={translateMessage("CalmLens on GitHub (opens in new tab)")}
                labelTooltip={false}
                variant="outline"
                size="lg"
                ripple={false}
                shape="circle"
              >
                <IconBrandGithub size={20} aria-hidden />
              </IconButton>
            </div>
            <Typography variant="bodyExtraSmall" tone="muted" className={styles.copyright}>
              © {copyrightYear} {TITLE}
              {translateMessage(". All rights reserved.")}
            </Typography>
          </div>

          <div className={styles.column}>
            <Label as="h2" tone="muted" className={styles.columnTitle}>
              {translateMessage("Legal")}
            </Label>
            <ul className={styles.links}>
              <li>
                <Link href="/terms">{translateMessage("Terms of Service")}</Link>
              </li>
              <li>
                <Link href="/copyright">{translateMessage("Copyright / DMCA")}</Link>
              </li>
              <li>
                <Link href="/cookies">{translateMessage("Cookie Policy")}</Link>
              </li>
              {DPA_PUBLISHED ? (
                <li>
                  <Link href="/dpa">{translateMessage("Data Processing Addendum")}</Link>
                </li>
              ) : null}
            </ul>
          </div>

          <div className={styles.column}>
            <Label as="h2" tone="muted" className={styles.columnTitle}>
              {translateMessage("Trust & privacy")}
            </Label>
            <ul className={styles.links}>
              <li>
                <Link href="/trust">{translateMessage("Trust")}</Link>
              </li>
              <li>
                <Link href="/privacy-policy">{translateMessage("Privacy Policy")}</Link>
              </li>
              <li>
                <Link href="/data-request">{translateMessage("Your data")}</Link>
              </li>
            </ul>
          </div>

          <div className={styles.column}>
            <Label as="h2" tone="muted" className={styles.columnTitle}>
              {translateMessage("Resources")}
            </Label>
            <ul className={styles.links}>
              <li>
                <Link href="/docs">{translateMessage("Documentation")}</Link>
              </li>
              <li>
                <Link href="/blog">{translateMessage("Blog")}</Link>
              </li>
              <li>
                <Link href="/docs/sdk">{translateMessage("SDKs")}</Link>
              </li>
              <li>
                <Link href="/docs/changelog">
                  {translateMessage("Changelog")}{" "}
                  <span className={styles.version}>{version.replace(/\.0$/, "")}</span>
                </Link>
              </li>
              <li>
                <Link href={STATUS_PAGE_URL}>{translateMessage("Status")}</Link>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default SiteFooter;
